import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
const EBOOK_COVER = 'https://cdn.magicpatterns.com/uploads/oAmUMx8hxSmZppAwH3vM1B/Image_for_mockup_Original.png';
const MESSAGES = [{
  name: 'Jessica',
  city: 'Atlanta',
  text: 'just bought her Blueprint!',
  time: '2 minutes ago'
}, {
  name: 'Lydia',
  city: 'Houston',
  text: "grabbed her copy—she's ready to save!",
  time: '5 minutes ago'
}, {
  name: 'Sophia',
  city: 'Chicago',
  text: 'downloaded her guide to fight back.',
  time: '8 minutes ago'
}, {
  name: 'Emily',
  city: 'Denver',
  text: 'refused to be overcharged—she got her copy!',
  time: '12 minutes ago'
}, {
  name: 'Amanda',
  city: 'Phoenix',
  text: 'joined 2,000+ women saving thousands.',
  time: '15 minutes ago'
}, {
  name: 'Rachel',
  city: 'Miami',
  text: 'secured her Blueprint—no more surprise bills.',
  time: '18 minutes ago'
}, {
  name: 'Laura',
  city: 'New York',
  text: 'just purchased—taking control of her healthcare costs.',
  time: '20 minutes ago'
}, {
  name: 'Hannah',
  city: 'Dallas',
  text: 'grabbed her copy—empowered and ready.',
  time: '25 minutes ago'
}, {
  name: 'Olivia',
  city: 'Seattle',
  text: 'joined the movement to stop overpaying.',
  time: '28 minutes ago'
}, {
  name: 'Grace',
  city: 'Boston',
  text: "downloaded her Blueprint—she's done with unfair bills.",
  time: '30 minutes ago'
}];
const MAX_NOTIFICATIONS = 5;
const DISPLAY_DURATION = 5500;
const MIN_INTERVAL = 45000;
const MAX_INTERVAL = 60000;
const SCROLL_THRESHOLD = 0.5;
const TIME_THRESHOLD = 20000;
function shuffleArray<T>(arr: T[]): T[] {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
export function SocialProofNotification() {
  const [currentMessage, setCurrentMessage] = useState<(typeof MESSAGES)[0] | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const shownCountRef = useRef(0);
  const messageQueueRef = useRef<typeof MESSAGES>(shuffleArray(MESSAGES));
  const messageIndexRef = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const getNextMessage = useCallback(() => {
    const queue = messageQueueRef.current;
    const msg = queue[messageIndexRef.current % queue.length];
    messageIndexRef.current++;
    if (messageIndexRef.current >= queue.length) {
      messageQueueRef.current = shuffleArray(MESSAGES);
      messageIndexRef.current = 0;
    }
    return msg;
  }, []);
  const showNotification = useCallback(() => {
    if (shownCountRef.current >= MAX_NOTIFICATIONS) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    const msg = getNextMessage();
    setCurrentMessage(msg);
    setIsVisible(true);
    shownCountRef.current++;
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, DISPLAY_DURATION);
  }, [getNextMessage]);
  const dismiss = useCallback(() => {
    setIsVisible(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);
  // Activation: scroll past 50% OR 20 seconds on page
  useEffect(() => {
    if (isActivated) return;
    const timeTimer = setTimeout(() => {
      setIsActivated(true);
    }, TIME_THRESHOLD);
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY / scrollHeight;
      if (scrolled >= SCROLL_THRESHOLD) {
        setIsActivated(true);
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      clearTimeout(timeTimer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isActivated]);
  // Once activated, start showing notifications
  useEffect(() => {
    if (!isActivated) return;
    // Show first one after a short delay
    const initialDelay = setTimeout(() => {
      showNotification();
    }, 2000);
    // Then rotate at random intervals
    const getRandomInterval = () => Math.floor(Math.random() * (MAX_INTERVAL - MIN_INTERVAL)) + MIN_INTERVAL;
    const scheduleNext = () => {
      intervalRef.current = setTimeout(() => {
        showNotification();
        scheduleNext();
      }, getRandomInterval());
    };
    const startTimer = setTimeout(() => {
      scheduleNext();
    }, 2000 + DISPLAY_DURATION + 1000);
    return () => {
      clearTimeout(initialDelay);
      clearTimeout(startTimer);
      if (intervalRef.current) clearTimeout(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [isActivated, showNotification]);
  return <div className="fixed bottom-4 left-4 z-40" style={{
    maxWidth: '340px'
  }} data-id="element-241">
      <AnimatePresence data-id="element-242">
        {isVisible && currentMessage && <motion.div initial={{
        opacity: 0,
        y: 30,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="bg-white rounded-xl shadow-lg border border-cream-200 p-3 flex items-start gap-3 cursor-pointer group" onClick={dismiss} data-id="element-243">
            <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 shadow-sm" data-id="element-244">
              <img src={EBOOK_COVER} alt="Stop Overpaying Medical Bills" className="w-full h-full object-cover" loading="lazy" width={56} height={56} decoding="async" data-id="element-245" />
            </div>

            <div className="flex-1 min-w-0" data-id="element-246">
              <p className="text-sm text-brown-900 leading-snug" data-id="element-247">
                <span className="font-bold" data-id="element-248">{currentMessage.name}</span> from{' '}
                {currentMessage.city}{' '}
                <span className="text-brown-700" data-id="element-249">{currentMessage.text}</span>
              </p>
              <p className="text-xs text-brown-400 mt-1" data-id="element-250">
                {currentMessage.time}
              </p>
            </div>

            <button onClick={e => {
          e.stopPropagation();
          dismiss();
        }} className="text-brown-300 hover:text-brown-600 transition-colors flex-shrink-0 mt-0.5" aria-label="Dismiss" data-id="element-251">
              <X className="w-3.5 h-3.5" data-id="element-252" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}